import { AdsAccountKeyword, KeywordConstraint } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import { httpsCallable } from 'firebase/functions';
import { useContext, useEffect, useState } from 'react';
import { functions } from '../Firebase';
import { UserContext } from '../contexts/UserContext';
import { useDebounce } from '../hooks/useDebounce';
import { distinct } from '../utils';
import Autocomplete from './Common/Autocomplete';
import Badge from './Common/Badge';

interface Props {
  filter?: any;
  selected: KeywordConstraint[];
  onChange: (_: KeywordConstraint[]) => void;
  readOnly?: boolean;
}
export default function KeywordsAutocomplete({
  filter,
  selected,
  onChange,
  readOnly,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<KeywordConstraint[]>();

  useEffect(() => {
    if (!selectedAccount) {
      setOptions(undefined);
      return;
    }

    const fn = httpsCallable<any, AdsAccountKeyword[]>(
      functions,
      'accounts-adsAccounts-getAdsAccountsKeywords',
    );

    setIsLoading(true);
    let relevant = true;
    fn({
      accountId: selectedAccount.id,
      provider: 'google',
      query: debouncedQuery,
      filter,
      limit: 100,
      skip: 0,
    })
      .then((res) => {
        if (!relevant) return;
        setOptions(res.data?.map((d: any) => ({ ...d, id: d._id })));
      })
      .catch((e) => {
        console.error(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      relevant = false;
    };
  }, [filter, debouncedQuery, selectedAccount]);

  return (
    <div className="flex flex-col gap-2">
      <Autocomplete<KeywordConstraint>
        title="Keywords (optional)"
        selected={{} as any}
        onChange={
          readOnly
            ? () => {}
            : (_selected) => {
                if (!_selected) return;

                // support adding multiple keywords at once, the option will be split by the character combination @~@
                if (_selected.keyword.includes('@~@')) {
                  const newUniqueKeywordsArray = _selected.keyword
                    .split('@~@')
                    .filter(Boolean)
                    .map((keyword) => ({
                      ..._selected,
                      keyword,
                    }));
                  onChange?.(
                    distinct(
                      [...selected, ...newUniqueKeywordsArray],
                      (a) => a.keyword,
                    ),
                  );
                } else {
                  onChange?.(
                    distinct(
                      [_selected, ...(selected || [])],
                      (a) => a.keyword,
                    ),
                  );
                }
                setQuery('');
              }
        }
        onInputChange={(t) => setQuery(t)}
        getOptionId={(t) => t.id}
        getValue={(t) => t.keyword}
        loading={!options || isLoading}
        options={(query.trim()
          ? [
              {
                id: query,
                keyword: query,
                provider: 'custom',
              } satisfies KeywordConstraint,
              ...(options || []),
            ]
          : options || []
        ).filter((x) => !selected?.find((s) => s.keyword === x.keyword))}
      />
      <div className="flex flex-wrap gap-2">
        {selected?.map((keyword) => (
          <Badge
            hideDot
            key={keyword.id}
            title={keyword.keyword}
            showDeleteButton={!readOnly}
            onDelete={
              readOnly
                ? undefined
                : () => {
                    onChange?.(
                      selected?.filter((k) => k.keyword !== keyword.keyword),
                    );
                  }
            }
          />
        ))}
      </div>
    </div>
  );
}
